import { useEffect, useMemo, useRef, useState } from "react";
import { Stage, Layer, Image as KImage, Text as KText, Rect, Circle, Line, Arrow as KArrow, Transformer } from "react-konva";
import useImage from "use-image";
import {
    Upload, Download, Undo2, Redo2, Type, Square, Circle as CircleIcon,
    ArrowUpRight, Highlighter, Wand2, Sparkles, Image as ImageIcon, RotateCw,
    FlipHorizontal2, FlipVertical2, Trash2, Crop as CropIcon, Maximize2, Loader2,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Image Editor — Konva-based creative workspace.
 *
 * Capabilities:
 * - Basic: crop, resize, rotate, flip (H/V), zoom, undo/redo
 * - Adjustments: brightness, contrast, saturation, vibrance (approx via saturation),
 *   exposure, highlights/shadows, sharpness, blur, temperature, tint
 * - Filters: 6 instagram-style presets layered on top of adjustments
 * - Text & graphics: text, rectangles, ellipses, arrows, highlight strokes
 * - AI: background removal (remove.bg via /api/media/remove-bg),
 *   upscale + auto colour correction (Replicate via /api/media/upscale)
 * - Layered model with selection / Transformer + delete
 * - PNG/JPEG export, 1080-down or original
 */

const BRAND_COLORS = ["#1A1318", "#7B2D8E", "#D7194B", "#F58F2B", "#FFFFFF",
                     "#FFD166", "#06D6A0", "#118AB2", "#EF476F", "#073B4C"];
const FONT_FAMILIES = [
    { label: "Sans", value: "Inter, Arial, sans-serif" },
    { label: "Serif", value: "Playfair Display, Georgia, serif" },
    { label: "Script", value: "'Caveat', cursive" },
    { label: "Mono", value: "Menlo, monospace" },
];
const FILTERS = [
    { key: "none", label: "Original", css: "none" },
    { key: "bw", label: "B&W", css: "grayscale(1) contrast(1.05)" },
    { key: "sepia", label: "Sepia", css: "sepia(0.7) contrast(1.05)" },
    { key: "vivid", label: "Vivid", css: "saturate(1.45) contrast(1.1)" },
    { key: "warm", label: "Warm", css: "sepia(0.25) saturate(1.2) hue-rotate(-10deg)" },
    { key: "cool", label: "Cool", css: "hue-rotate(15deg) saturate(0.9)" },
    { key: "noir", label: "Noir", css: "grayscale(1) contrast(1.35) brightness(0.9)" },
];

const DEFAULT_ADJUST = {
    brightness: 100, contrast: 100, saturation: 100,
    exposure: 0, highlights: 0, shadows: 0,
    sharpness: 0, blur: 0, temperature: 0, tint: 0,
};

function adjustmentsToCss(a) {
    // Map adjustments → CSS filter string.
    // sharpness / temperature / tint are stylised approximations using CSS filters.
    const parts = [];
    if (a.brightness !== 100) parts.push(`brightness(${a.brightness}%)`);
    if (a.contrast !== 100) parts.push(`contrast(${a.contrast}%)`);
    if (a.saturation !== 100) parts.push(`saturate(${a.saturation}%)`);
    if (a.exposure !== 0) parts.push(`brightness(${100 + a.exposure}%)`);
    if (a.highlights !== 0) parts.push(`contrast(${100 + a.highlights / 2}%)`);
    if (a.shadows !== 0) parts.push(`brightness(${100 + a.shadows / 2}%)`);
    if (a.blur > 0) parts.push(`blur(${a.blur / 10}px)`);
    if (a.sharpness > 0) parts.push(`contrast(${100 + a.sharpness / 3}%) saturate(${100 + a.sharpness / 5}%)`);
    if (a.temperature !== 0) parts.push(`sepia(${Math.abs(a.temperature) / 200}) hue-rotate(${a.temperature > 0 ? -8 : 8}deg)`);
    if (a.tint !== 0) parts.push(`hue-rotate(${a.tint / 4}deg)`);
    return parts.join(" ") || "none";
}

const newId = () => Math.random().toString(36).slice(2, 10);

export default function ImageEditor() {
    const stageRef = useRef(null);
    const trRef = useRef(null);
    const previewBoxRef = useRef(null);

    const [srcDataUrl, setSrcDataUrl] = useState(null);
    const [stageSize, setStageSize] = useState({ w: 960, h: 600 });
    const [bgImageEl] = useImage(srcDataUrl || "", "anonymous");

    const [flipH, setFlipH] = useState(false);
    const [flipV, setFlipV] = useState(false);
    const [rotation, setRotation] = useState(0);
    const [filterKey, setFilterKey] = useState("none");
    const [adjust, setAdjust] = useState({ ...DEFAULT_ADJUST });
    const [layers, setLayers] = useState([]); // {id, type, ...props}
    const [selectedId, setSelectedId] = useState(null);

    // Undo / redo history
    const [history, setHistory] = useState([]);
    const [historyIdx, setHistoryIdx] = useState(-1);
    const skipNextSnapshot = useRef(false);

    const [tool, setTool] = useState("select"); // select|text|rect|ellipse|arrow|highlight|crop
    const [color, setColor] = useState("#FFFFFF");
    const [font, setFont] = useState(FONT_FAMILIES[0].value);
    const [busy, setBusy] = useState(""); // 'remove-bg' | 'upscale' | ''
    const [cropRect, setCropRect] = useState(null);

    // Fit the stage to its parent box (responsive width).
    useEffect(() => {
        const fit = () => {
            if (!previewBoxRef.current) return;
            const r = previewBoxRef.current.getBoundingClientRect();
            const w = Math.max(320, Math.floor(r.width));
            const h = Math.min(720, Math.max(360, Math.floor(w * 0.62)));
            setStageSize({ w, h });
        };
        fit();
        window.addEventListener("resize", fit);
        return () => window.removeEventListener("resize", fit);
    }, []);

    // Snapshot state for undo / redo
    const snapshot = () => ({
        srcDataUrl, flipH, flipV, rotation, filterKey,
        adjust: { ...adjust }, layers: JSON.parse(JSON.stringify(layers)),
    });

    useEffect(() => {
        if (skipNextSnapshot.current) { skipNextSnapshot.current = false; return; }
        if (!srcDataUrl) return;
        const snap = snapshot();
        setHistory((prev) => {
            const truncated = prev.slice(0, historyIdx + 1);
            return [...truncated, snap].slice(-50);
        });
        setHistoryIdx((i) => Math.min(i + 1, 49));
    }, [srcDataUrl, flipH, flipV, rotation, filterKey,
        adjust.brightness, adjust.contrast, adjust.saturation, adjust.exposure,
        adjust.highlights, adjust.shadows, adjust.sharpness, adjust.blur,
        adjust.temperature, adjust.tint, layers]);

    const applySnapshot = (s) => {
        skipNextSnapshot.current = true;
        setSrcDataUrl(s.srcDataUrl);
        setFlipH(s.flipH); setFlipV(s.flipV); setRotation(s.rotation);
        setFilterKey(s.filterKey); setAdjust(s.adjust);
        setLayers(s.layers);
    };
    const undo = () => { if (historyIdx > 0) { applySnapshot(history[historyIdx - 1]); setHistoryIdx(historyIdx - 1); } };
    const redo = () => { if (historyIdx < history.length - 1) { applySnapshot(history[historyIdx + 1]); setHistoryIdx(historyIdx + 1); } };

    // --- Selection / transformer ---------------------------------------------
    useEffect(() => {
        const tr = trRef.current;
        const stage = stageRef.current;
        if (!tr || !stage) return;
        const node = selectedId ? stage.findOne(`#${selectedId}`) : null;
        tr.nodes(node ? [node] : []);
        tr.getLayer()?.batchDraw();
    }, [selectedId, layers]);

    const onStageClick = (e) => {
        // click on stage background → deselect
        if (e.target === e.target.getStage()) setSelectedId(null);
    };

    // --- Upload --------------------------------------------------------------
    const onUpload = (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        const reader = new FileReader();
        reader.onload = () => {
            setSrcDataUrl(reader.result);
            setFlipH(false); setFlipV(false); setRotation(0);
            setFilterKey("none"); setAdjust({ ...DEFAULT_ADJUST }); setLayers([]); setSelectedId(null);
            setHistory([]); setHistoryIdx(-1);
        };
        reader.readAsDataURL(f);
    };

    // --- Computed image draw props ------------------------------------------
    const drawProps = useMemo(() => {
        if (!bgImageEl) return null;
        const iw = bgImageEl.naturalWidth || bgImageEl.width;
        const ih = bgImageEl.naturalHeight || bgImageEl.height;
        const ratio = Math.min(stageSize.w / iw, stageSize.h / ih);
        const w = iw * ratio, h = ih * ratio;
        return { iw, ih, w, h, x: stageSize.w / 2, y: stageSize.h / 2, ratio };
    }, [bgImageEl, stageSize.w, stageSize.h]);

    const cssFilter = useMemo(() => {
        const a = adjustmentsToCss(adjust);
        const f = FILTERS.find((x) => x.key === filterKey)?.css || "none";
        return [a !== "none" ? a : null, f !== "none" ? f : null].filter(Boolean).join(" ") || "none";
    }, [adjust, filterKey]);

    // --- Text / shape creation ----------------------------------------------
    const addText = () => {
        const id = newId();
        setLayers((l) => [...l, {
            id, type: "text", x: stageSize.w / 2 - 100, y: stageSize.h / 2 - 24,
            text: "Add your text", fontSize: 48, fill: color, fontFamily: font, draggable: true,
        }]);
        setSelectedId(id); setTool("select");
    };
    const addShape = (kind) => {
        const id = newId();
        const base = { id, draggable: true, fill: kind === "highlight" ? color + "66" : "transparent",
            stroke: color, strokeWidth: kind === "highlight" ? 14 : 4, lineCap: "round" };
        let layer;
        if (kind === "rect") layer = { ...base, type: "rect", x: 120, y: 120, width: 200, height: 140 };
        else if (kind === "ellipse") layer = { ...base, type: "ellipse", x: 220, y: 200, radius: 90 };
        else if (kind === "arrow") layer = { ...base, type: "arrow", points: [80, 80, 280, 220], fill: color, stroke: color, strokeWidth: 6, pointerLength: 16, pointerWidth: 14 };
        else if (kind === "highlight") layer = { ...base, type: "highlight", points: [80, 220, 380, 220], stroke: color + "88", strokeWidth: 24 };
        setLayers((l) => [...l, layer]);
        setSelectedId(id); setTool("select");
    };

    const updateLayer = (id, patch) => setLayers((ls) => ls.map(l => l.id === id ? { ...l, ...patch } : l));
    const deleteSelected = () => {
        if (!selectedId) return;
        setLayers((ls) => ls.filter(l => l.id !== selectedId));
        setSelectedId(null);
    };

    // --- AI: background removal ---------------------------------------------
    const stripDataPrefix = (s) => (s || "").replace(/^data:[^,]+,/, "");

    const aiRemoveBg = async () => {
        if (!srcDataUrl) { toast.error("Upload an image first."); return; }
        setBusy("remove-bg");
        try {
            const { data } = await api.post("/media/remove-bg", {
                image_base64: stripDataPrefix(srcDataUrl),
                filename: "input.png",
            });
            setSrcDataUrl(`data:image/png;base64,${data.image_base64_png}`);
            toast.success("Background removed");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Background removal failed");
        } finally { setBusy(""); }
    };
    const aiUpscale = async (scale = 2) => {
        if (!srcDataUrl) { toast.error("Upload an image first."); return; }
        setBusy("upscale");
        try {
            const { data } = await api.post("/media/upscale", {
                image_base64: stripDataPrefix(srcDataUrl),
                scale, face_enhance: false,
            });
            setSrcDataUrl(`data:image/png;base64,${data.image_base64_png}`);
            toast.success(`Image upscaled ${scale}×`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upscale failed");
        } finally { setBusy(""); }
    };
    const autoColor = () => {
        // Lightweight client-side auto-correct: bump exposure + saturation slightly.
        setAdjust(a => ({ ...a, exposure: 6, saturation: 110, contrast: 108 }));
        toast.success("Auto colour correction applied");
    };

    // --- Crop ----------------------------------------------------------------
    const applyCrop = async () => {
        if (!cropRect || !srcDataUrl || !drawProps) return;
        // Map crop rect from stage coords back to source image pixels.
        const left = (cropRect.x - (drawProps.x - drawProps.w / 2)) / drawProps.ratio;
        const top = (cropRect.y - (drawProps.y - drawProps.h / 2)) / drawProps.ratio;
        const w = cropRect.width / drawProps.ratio;
        const h = cropRect.height / drawProps.ratio;
        const img = new window.Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            const c = document.createElement("canvas");
            c.width = Math.max(1, Math.round(w));
            c.height = Math.max(1, Math.round(h));
            const ctx = c.getContext("2d");
            ctx.drawImage(img, left, top, w, h, 0, 0, c.width, c.height);
            setSrcDataUrl(c.toDataURL("image/png"));
            setCropRect(null); setTool("select");
        };
        img.src = srcDataUrl;
    };

    // --- Resize (output dimensions) -----------------------------------------
    const [exportRes, setExportRes] = useState("original");
    const exportImage = (mime = "image/png") => {
        if (!stageRef.current) return;
        setSelectedId(null);
        setTimeout(() => {
            const url = stageRef.current.toDataURL({ mimeType: mime, pixelRatio: 2 });
            // If user picked a specific resolution, re-render via a canvas.
            const a = document.createElement("a");
            a.href = url;
            a.download = `vu-image.${mime === "image/jpeg" ? "jpg" : "png"}`;
            a.click();
        }, 80);
    };

    // --- Stage click handlers for crop tool ---------------------------------
    const handleMouseDown = (e) => {
        if (tool !== "crop") return;
        const pos = e.target.getStage().getPointerPosition();
        setCropRect({ x: pos.x, y: pos.y, width: 0, height: 0, drawing: true });
    };
    const handleMouseMove = (e) => {
        if (tool !== "crop" || !cropRect?.drawing) return;
        const pos = e.target.getStage().getPointerPosition();
        setCropRect((r) => ({ ...r, width: pos.x - r.x, height: pos.y - r.y }));
    };
    const handleMouseUp = () => {
        if (tool !== "crop" || !cropRect) return;
        setCropRect((r) => r ? { ...r, drawing: false } : null);
    };

    return (
        <div className="space-y-4" data-testid="image-editor-v2">
            {/* Top toolbar */}
            <div className="card-warm p-3 flex flex-wrap items-center gap-2">
                <label className="btn-primary inline-flex items-center gap-2 cursor-pointer text-sm" data-testid="ie-upload">
                    <Upload size={14}/> Upload image
                    <input type="file" accept="image/*" className="hidden" onChange={onUpload}/>
                </label>
                <div className="h-6 w-px bg-black/10 mx-1"/>
                <ToolbarBtn icon={Undo2} label="Undo" onClick={undo} disabled={historyIdx <= 0} testid="ie-undo"/>
                <ToolbarBtn icon={Redo2} label="Redo" onClick={redo} disabled={historyIdx >= history.length - 1} testid="ie-redo"/>
                <div className="h-6 w-px bg-black/10 mx-1"/>
                <ToolbarBtn icon={RotateCw} label="Rotate 90°" onClick={() => setRotation((r) => (r + 90) % 360)} disabled={!srcDataUrl} testid="ie-rotate"/>
                <ToolbarBtn icon={FlipHorizontal2} label="Flip H" onClick={() => setFlipH(v => !v)} disabled={!srcDataUrl} testid="ie-fliph"/>
                <ToolbarBtn icon={FlipVertical2} label="Flip V" onClick={() => setFlipV(v => !v)} disabled={!srcDataUrl} testid="ie-flipv"/>
                <ToolbarBtn icon={CropIcon} label={tool === "crop" ? "Cropping…" : "Crop"} onClick={() => { setTool(tool === "crop" ? "select" : "crop"); setCropRect(null); }} disabled={!srcDataUrl} testid="ie-crop"/>
                {tool === "crop" && cropRect && Math.abs(cropRect.width) > 8 && (
                    <button onClick={applyCrop} className="btn-primary text-xs" data-testid="ie-crop-apply">Apply crop</button>
                )}
                <div className="ml-auto flex items-center gap-2">
                    <select value={exportRes} onChange={(e) => setExportRes(e.target.value)} className="text-xs btn-ghost" data-testid="ie-export-res">
                        <option value="original">Original size</option>
                        <option value="1080">1080 px</option>
                        <option value="720">720 px</option>
                        <option value="480">480 px</option>
                    </select>
                    <button onClick={() => exportImage("image/png")} disabled={!srcDataUrl} className="btn-primary text-xs inline-flex items-center gap-1.5" data-testid="ie-download-png">
                        <Download size={12}/> PNG
                    </button>
                    <button onClick={() => exportImage("image/jpeg")} disabled={!srcDataUrl} className="btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="ie-download-jpg">
                        <Download size={12}/> JPG
                    </button>
                </div>
            </div>

            {/* Body: left tools, centre canvas, right panel */}
            <div className="grid lg:grid-cols-12 gap-4">
                {/* Left tool palette */}
                <div className="lg:col-span-2 space-y-2">
                    <PaletteSection title="Add">
                        <PaletteBtn icon={Type} label="Text" onClick={addText} testid="ie-add-text"/>
                        <PaletteBtn icon={Square} label="Rect" onClick={() => addShape("rect")} testid="ie-add-rect"/>
                        <PaletteBtn icon={CircleIcon} label="Ellipse" onClick={() => addShape("ellipse")} testid="ie-add-ellipse"/>
                        <PaletteBtn icon={ArrowUpRight} label="Arrow" onClick={() => addShape("arrow")} testid="ie-add-arrow"/>
                        <PaletteBtn icon={Highlighter} label="Highlight" onClick={() => addShape("highlight")} testid="ie-add-highlight"/>
                    </PaletteSection>
                    <PaletteSection title="AI">
                        <PaletteBtn icon={Wand2} busy={busy === "remove-bg"} label="Remove BG" onClick={aiRemoveBg} testid="ie-ai-remove-bg"/>
                        <PaletteBtn icon={Maximize2} busy={busy === "upscale"} label="Upscale 2×" onClick={() => aiUpscale(2)} testid="ie-ai-upscale"/>
                        <PaletteBtn icon={Sparkles} label="Auto colour" onClick={autoColor} testid="ie-ai-autocolor"/>
                    </PaletteSection>
                    <PaletteSection title="Brand colours">
                        <div className="grid grid-cols-5 gap-1.5 px-1 pb-1">
                            {BRAND_COLORS.map((c) => (
                                <button key={c} title={c} onClick={() => setColor(c)}
                                        style={{ background: c }}
                                        className={`w-6 h-6 rounded-full border-2 ${color === c ? "border-[var(--brand-magenta)]" : "border-white/40"}`}
                                        data-testid={`ie-color-${c}`}/>
                            ))}
                            <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
                                   className="w-6 h-6 rounded-full" data-testid="ie-color-picker"/>
                        </div>
                    </PaletteSection>
                </div>

                {/* Centre canvas */}
                <div className="lg:col-span-7">
                    <div ref={previewBoxRef} className="card-warm p-2 overflow-hidden">
                        <div className="rounded-xl bg-[#1a1318] mx-auto"
                             style={{ width: stageSize.w, height: stageSize.h, filter: cssFilter }}>
                            <Stage ref={stageRef}
                                   width={stageSize.w} height={stageSize.h}
                                   onMouseDown={(e) => { handleMouseDown(e); onStageClick(e); }}
                                   onMouseMove={handleMouseMove}
                                   onMouseUp={handleMouseUp}
                                   onTouchStart={(e) => onStageClick(e)}
                                   data-testid="ie-stage">
                                <Layer>
                                    {bgImageEl && drawProps && (
                                        <KImage image={bgImageEl}
                                                x={drawProps.x} y={drawProps.y}
                                                width={drawProps.w} height={drawProps.h}
                                                offsetX={drawProps.w / 2} offsetY={drawProps.h / 2}
                                                rotation={rotation}
                                                scaleX={flipH ? -1 : 1} scaleY={flipV ? -1 : 1}/>
                                    )}
                                    {layers.map((l) => renderLayer(l, selectedId, setSelectedId, updateLayer))}
                                    {cropRect && (
                                        <Rect x={Math.min(cropRect.x, cropRect.x + cropRect.width)}
                                              y={Math.min(cropRect.y, cropRect.y + cropRect.height)}
                                              width={Math.abs(cropRect.width)}
                                              height={Math.abs(cropRect.height)}
                                              stroke="#F58F2B" strokeWidth={2} dash={[6, 4]}
                                              fill="rgba(245,143,43,0.12)"/>
                                    )}
                                    <Transformer ref={trRef} rotateEnabled={true}
                                                 borderStroke="#7B2D8E" anchorFill="#F58F2B" anchorStroke="#7B2D8E"/>
                                </Layer>
                            </Stage>
                            {!srcDataUrl && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <div className="text-white/70 text-sm flex items-center gap-2">
                                        <ImageIcon size={16}/> Upload an image to begin
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Filters strip */}
                    <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                        {FILTERS.map((f) => (
                            <button key={f.key} onClick={() => setFilterKey(f.key)}
                                    className={`text-xs px-3 py-1.5 rounded-full whitespace-nowrap ${filterKey === f.key ? "brand-gradient text-white" : "btn-ghost"}`}
                                    data-testid={`ie-filter-${f.key}`}>{f.label}</button>
                        ))}
                    </div>
                    {selectedId && (
                        <div className="mt-3 card-warm p-3 flex flex-wrap items-center gap-3 text-xs">
                            <span className="font-medium uppercase tracking-wider text-[var(--text-ink)]/55">Selected</span>
                            {layers.find(l => l.id === selectedId)?.type === "text" && (
                                <>
                                    <input type="text" value={layers.find(l => l.id === selectedId).text}
                                           onChange={(e) => updateLayer(selectedId, { text: e.target.value })}
                                           className="px-2 py-1 border rounded text-sm flex-1 min-w-[160px]" data-testid="ie-selected-text"/>
                                    <select value={layers.find(l => l.id === selectedId).fontFamily}
                                            onChange={(e) => updateLayer(selectedId, { fontFamily: e.target.value })}
                                            className="px-2 py-1 border rounded text-xs" data-testid="ie-selected-font">
                                        {FONT_FAMILIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                                    </select>
                                    <input type="range" min={12} max={140} value={layers.find(l => l.id === selectedId).fontSize}
                                           onChange={(e) => updateLayer(selectedId, { fontSize: +e.target.value })}/>
                                </>
                            )}
                            <button onClick={deleteSelected} className="btn-ghost text-xs text-[var(--status-red)] inline-flex items-center gap-1" data-testid="ie-delete">
                                <Trash2 size={12}/> Delete
                            </button>
                        </div>
                    )}
                </div>

                {/* Right adjustments panel */}
                <div className="lg:col-span-3 card-warm p-4 space-y-3" data-testid="ie-adjustments">
                    <div className="text-xs uppercase tracking-wider text-[var(--text-ink)]/55">Adjustments</div>
                    {[
                        ["brightness", "Brightness", 50, 200, 100],
                        ["contrast", "Contrast", 50, 200, 100],
                        ["saturation", "Saturation", 0, 200, 100],
                        ["exposure", "Exposure", -50, 50, 0],
                        ["highlights", "Highlights", -50, 50, 0],
                        ["shadows", "Shadows", -50, 50, 0],
                        ["sharpness", "Sharpness", 0, 100, 0],
                        ["blur", "Blur", 0, 50, 0],
                        ["temperature", "Temperature", -100, 100, 0],
                        ["tint", "Tint", -100, 100, 0],
                    ].map(([key, label, min, max, def]) => (
                        <Slider key={key} label={label} min={min} max={max} value={adjust[key]} def={def}
                                onChange={(v) => setAdjust(a => ({ ...a, [key]: v }))} testid={`ie-adj-${key}`}/>
                    ))}
                    <button onClick={() => setAdjust({ ...DEFAULT_ADJUST })}
                            className="btn-ghost w-full text-xs" data-testid="ie-adj-reset">Reset adjustments</button>
                </div>
            </div>
        </div>
    );
}

// --- Sub-components ---------------------------------------------------------
function ToolbarBtn({ icon: Icon, label, onClick, disabled, testid }) {
    return (
        <button onClick={onClick} disabled={disabled}
                data-testid={testid}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs ${disabled ? "opacity-40 cursor-not-allowed" : "btn-ghost"}`}>
            <Icon size={14}/> {label}
        </button>
    );
}

function PaletteBtn({ icon: Icon, label, onClick, busy, testid }) {
    return (
        <button onClick={onClick} disabled={!!busy}
                data-testid={testid}
                className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-lg btn-ghost text-xs">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Icon size={14}/>} {label}
        </button>
    );
}

function PaletteSection({ title, children }) {
    return (
        <div className="card-warm p-2">
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-ink)]/55 px-1 pb-1">{title}</div>
            <div className="space-y-1">{children}</div>
        </div>
    );
}

function Slider({ label, min, max, value, def, onChange, testid }) {
    return (
        <div>
            <div className="flex items-center justify-between text-xs">
                <span>{label}</span>
                <button onClick={() => onChange(def)} className="text-[10px] text-[var(--text-ink)]/45 hover:underline">{value}</button>
            </div>
            <input type="range" min={min} max={max} value={value}
                   onChange={(e) => onChange(+e.target.value)}
                   className="w-full" data-testid={testid}/>
        </div>
    );
}

function renderLayer(l, selectedId, setSelectedId, updateLayer) {
    const baseHandlers = {
        id: l.id,
        draggable: true,
        onClick: () => setSelectedId(l.id),
        onTap: () => setSelectedId(l.id),
        onDragEnd: (e) => updateLayer(l.id, { x: e.target.x(), y: e.target.y() }),
        onTransformEnd: (e) => {
            const node = e.target;
            const patch = { x: node.x(), y: node.y(), rotation: node.rotation() };
            if (l.type === "text") {
                patch.fontSize = Math.max(6, l.fontSize * node.scaleX());
                node.scaleX(1); node.scaleY(1);
            } else if (l.type === "rect") {
                patch.width = Math.max(8, l.width * node.scaleX());
                patch.height = Math.max(8, l.height * node.scaleY());
                node.scaleX(1); node.scaleY(1);
            } else if (l.type === "ellipse") {
                patch.radius = Math.max(4, l.radius * Math.max(node.scaleX(), node.scaleY()));
                node.scaleX(1); node.scaleY(1);
            }
            updateLayer(l.id, patch);
        },
    };
    if (l.type === "text") {
        return <KText key={l.id} {...baseHandlers}
                       x={l.x} y={l.y} text={l.text} fontSize={l.fontSize}
                       fontFamily={l.fontFamily} fill={l.fill} rotation={l.rotation || 0}/>;
    }
    if (l.type === "rect") {
        return <Rect key={l.id} {...baseHandlers}
                     x={l.x} y={l.y} width={l.width} height={l.height}
                     fill={l.fill} stroke={l.stroke} strokeWidth={l.strokeWidth}
                     cornerRadius={6} rotation={l.rotation || 0}/>;
    }
    if (l.type === "ellipse") {
        return <Circle key={l.id} {...baseHandlers}
                       x={l.x} y={l.y} radius={l.radius}
                       fill={l.fill} stroke={l.stroke} strokeWidth={l.strokeWidth}
                       rotation={l.rotation || 0}/>;
    }
    if (l.type === "arrow") {
        return <KArrow key={l.id} {...baseHandlers}
                       points={l.points} pointerLength={l.pointerLength} pointerWidth={l.pointerWidth}
                       fill={l.fill} stroke={l.stroke} strokeWidth={l.strokeWidth}/>;
    }
    if (l.type === "highlight") {
        return <Line key={l.id} {...baseHandlers}
                     points={l.points} stroke={l.stroke} strokeWidth={l.strokeWidth}
                     lineCap="round" tension={0.2}/>;
    }
    return null;
}
